/**
 * Utility functions to format location data consistently across the application
 */

/**
 * Formats a city name by replacing special characters with spaces
 * and capitalizing each word (first letter uppercase, rest lowercase)
 */
export const formatCity = (city: string): string => {
  if (!city) return ''

  return city
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

/**
 * Formats a state name by replacing special characters with spaces
 * and converting to uppercase
 */
export const formatState = (state: string): string => {
  if (!state) return ''

  return state
    .replace(/[^a-zA-Z0-9\s]/g, ' ')
    .toUpperCase()
    .trim()
}

/**
 * Formats both city and state and combines them into a location string
 */
export const formatLocation = (city: string, state: string): string => {
  const formattedCity = formatCity(city)
  const formattedState = formatState(state)

  if (formattedCity && formattedState) {
    return `${formattedCity}, ${formattedState}`
  }

  if (formattedCity) return formattedCity
  if (formattedState) return formattedState

  return ''
}

/**
 * Decodes and formats location parameters from URL
 */
export const formatLocationFromUrl = (cityParam?: string, stateParam?: string) => {
  const decodedCity = cityParam ? decodeURIComponent(cityParam) : ''
  const decodedState = stateParam ? decodeURIComponent(stateParam) : ''

  return {
    city: formatCity(decodedCity),
    state: formatState(decodedState),
    location: formatLocation(decodedCity, decodedState),
  }
}
